import { ComponentType } from 'preact'
import { YogaWidgetBase, InitData } from '../../core'
import { SchedulePanel } from './SchedulePanel'

/**
 * <holdstyring-schedule> Web Component
 *
 * Displays a list of upcoming classes with booking functionality.
 *
 * Attributes:
 *   api-key: Required. Your Holdstyring API key.
 *   tenant-slug: Required on external sites.
 *   hold-type-slugs="yoga,pilates": Optional. Only show classes carrying these
 *     hold-mærkater (comma-separated).
 *   template-id="xxx": Optional. Only show classes from this one class template
 *     (use when a page should show a single class/programme).
 *   weeks="4": Optional. Only show classes within the next N weeks (1-52).
 *   title="Our classes": Optional. Heading above the widget (default: none).
 *   view-toggle="true": Optional. Adds the studio site's Classes/Calendar switch,
 *     so an embedded page can offer both views instead of only the list.
 *   default-view="calendar": Optional. Which view the switch starts on (default: list).
 *   theme-*: Optional. See HoldstyringWidgetBase.observedAttributes.
 *
 * With no filter/weeks attributes it renders every upcoming class (bounded by
 * each template's booking horizon).
 *
 * Events: none. (Login/booking events come from the login and bookings
 *   widgets — see the widget guide.)
 *
 * Example:
 *   <holdstyring-schedule api-key="wk_live_xxx" template-id="abc" weeks="4"></holdstyring-schedule>
 */
class HoldstyringScheduleElement extends YogaWidgetBase {
  protected getComponent(): ComponentType<{ initData: InitData }> {
    return SchedulePanel
  }

  // Supported HTML attributes: hold-type-slugs (comma list), template-id, weeks,
  // title, view-toggle, default-view.
  protected getWidgetProps() {
    const slugsRaw = this.getAttribute('hold-type-slugs') || ''
    const holdTypeSlugs = slugsRaw.split(',').map(s => s.trim()).filter(Boolean)
    const templateId = (this.getAttribute('template-id') || '').trim() || null
    const weeksRaw = this.getAttribute('weeks')
    const weeks = weeksRaw && /^\d+$/.test(weeksRaw)
      ? Math.max(1, Math.min(52, parseInt(weeksRaw, 10)))
      : null
    return {
      holdTypeSlugs,
      templateId,
      weeks,
      title: this.getAttribute('title') || null,
      // Opt-in: existing embeds must keep rendering the bare list they had.
      viewToggle: this.getAttribute('view-toggle') === 'true',
      defaultView: this.getAttribute('default-view') === 'calendar' ? 'calendar' as const : 'list' as const,
      // guests-only="true": gæste-fokuseret booking (stepperen vises åben).
      guestsOnly: this.getAttribute('guests-only') === 'true',
    }
  }
}

// Register the custom element
if (!customElements.get('holdstyring-schedule')) {
  customElements.define('holdstyring-schedule', HoldstyringScheduleElement)
}

export { HoldstyringScheduleElement }
